import React from "react";
import { Card } from "react-bootstrap";
import { getArrowCell } from "./helpers";

const ExecutiveReport: React.FC = () => (
  <Card className="padding-20px raw-email-html executive-report-styling-container">
    <p className="m-0 date-subtitle">Week of May 20, 2025</p>
    <hr />
    <div className="sample-email-reports-body">
      <h2 id="key-insights-this-week" className="section-title">
        🔍 KEY INSIGHTS THIS WEEK
      </h2>
      <p>
        <strong>Digital Transformation Progress</strong>: Technology teams
        report strong momentum (energy 4.2/5) on cloud migration initiative,
        with 3 critical systems successfully transitioned this week while
        maintaining 99.9% uptime for existing operations
      </p>
      <p>
        <strong>Customer Service Capacity</strong>: Support teams managing
        increased ticket volumes (+35% YoY) report strain on response times,
        with several team members working overtime to maintain SLA commitments
        despite resource constraints
      </p>
      <p>
        <strong>Sales Pipeline Acceleration</strong>: Sales team showing highest
        engagement scores (4.3/5) following successful product launch, with
        multiple enterprise deals advancing to final stages and new vertical
        market opportunities identified
      </p>
      <h2 id="organizational-health" className="section-title">
        📊 ORGANIZATIONAL HEALTH
      </h2>
      <table>
        <colgroup>
          <col />
          <col />
          <col />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th>
              <strong>Metric</strong>
            </th>
            <th>
              <strong>Score</strong>
            </th>
            <th className="trend">
              <strong>Trend</strong>
            </th>
            <th>
              <strong>Change</strong>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Overall</td>
            <td>3.8/5</td>
            {getArrowCell(0.15)}
            <td>+0.15</td>
          </tr>
          <tr className="tr-alt">
            <td>Energy</td>
            <td>3.7/5</td>
            {getArrowCell(0.12)}
            <td>+0.12</td>
          </tr>
          <tr>
            <td>Progress</td>
            <td>3.9/5</td>
            {getArrowCell(0.08)}
            <td>+0.08</td>
          </tr>
          <tr className="tr-alt">
            <td>Readiness</td>
            <td>4.1/5</td>
            {getArrowCell(0.3)}
            <td>+0.30</td>
          </tr>
          <tr>
            <td>Burnout Risk</td>
            <td>19%</td>
            {getArrowCell(-3)}
            <td>-3%</td>
          </tr>
          <tr className="tr-alt">
            <td>Turnover Risk</td>
            <td>16%</td>
            {getArrowCell(0)}
            <td>0%</td>
          </tr>
          <tr>
            <td>Completion Rate</td>
            <td>52%</td>
            {getArrowCell(4)}
            <td>+4%</td>
          </tr>
        </tbody>
      </table>
      <h2 id="department-status-overview" className="section-title">
        DEPARTMENT STATUS OVERVIEW
      </h2>
      <h3 id="celebrate" className="dept-category celebrate">
        <strong>🌟 CELEBRATE</strong>
      </h3>
      <table>
        <colgroup>
          <col />
          <col />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th className="department-name">
              <strong>Department</strong>
            </th>
            <th>
              <strong>Score</strong>
            </th>
            <th>
              <strong>Key Win</strong>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <strong>Sales</strong>
            </td>
            <td>4.3/5</td>
            <td>
              Exceeded Q2 pipeline targets by 22% while successfully onboarding
              5 new team members
            </td>
          </tr>
          <tr className="tr-alt">
            <td>
              <strong>Product</strong>
            </td>
            <td>4.2/5</td>
            <td>
              Launched mobile app update ahead of schedule with positive
              customer feedback (4.7/5 rating)
            </td>
          </tr>
          <tr>
            <td>
              <strong>Operations</strong>
            </td>
            <td>4.1/5</td>
            <td>
              Implemented new inventory management system reducing processing
              time by 40%
            </td>
          </tr>
        </tbody>
      </table>
      <h3 id="needs-support" className="dept-category needs-support">
        <strong>⚠️ NEEDS SUPPORT</strong>
      </h3>
      <table>
        <colgroup>
          <col />
          <col />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th className="department-name">
              <strong>Department</strong>
            </th>
            <th>
              <strong>Score</strong>
            </th>
            <th>
              <strong>Critical Need</strong>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <strong>Customer Support</strong>
            </td>
            <td>3.5/5</td>
            <td>
              Team reporting high ticket volumes with average response time
              increasing; staffing review needed
            </td>
          </tr>
        </tbody>
      </table>
      <h3 id="holding-steady" className="dept-category holding-steady">
        <strong>➡️ HOLDING STEADY</strong>
      </h3>
      <table>
        <colgroup>
          <col />
          <col />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th className="department-name">
              <strong>Department</strong>
            </th>
            <th>
              <strong>Score</strong>
            </th>
            <th>
              <strong>Focus Area</strong>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <strong>Engineering</strong>
            </td>
            <td>3.9/5</td>
            <td>
              Maintaining stability while managing technical debt reduction
              alongside new feature development
            </td>
          </tr>
          <tr className="tr-alt">
            <td>
              <strong>Marketing</strong>
            </td>
            <td>3.8/5</td>
            <td>Executing multi-channel campaign for Q3 product launch</td>
          </tr>
        </tbody>
      </table>
      <h2 id="strategic-patterns" className="section-title">
        🔄 STRATEGIC PATTERNS
      </h2>
      <h3 id="scaling-challenges-during-growth-phase">
        <strong>Scaling Challenges During Growth Phase</strong>
      </h3>
      <ul className="check-in-response-list column-gap-12px">
        <li>
          <p>
            <strong>Pattern</strong>: Multiple departments report resource
            constraints while managing increased workload from business growth.
          </p>
        </li>
        <li>
          <p>
            <strong>Evidence</strong>: Customer Support handling 35% more
            tickets; Operations team managing 3 simultaneous system
            implementations; Engineering balancing maintenance with new
            development across 5 projects.
          </p>
        </li>
        <li>
          <p>
            <strong>Business Impact</strong>: Risk of service degradation,
            employee burnout, and missed growth opportunities if scaling isn't
            addressed systematically.
          </p>
        </li>
        <li>
          <p>
            <strong>Leadership Opportunity</strong>: Evaluate departmental
            capacity planning against growth projections; consider phased hiring
            plan or process automation initiatives.
          </p>
        </li>
      </ul>

      <h3 id="cross-functional-collaboration-success">
        <strong>Cross-Functional Collaboration Success</strong>
      </h3>
      <ul className="check-in-response-list column-gap-12px">
        <li>
          <p>
            <strong>Pattern</strong>: Teams report improved outcomes when
            working across departments, particularly between Product,
            Engineering, and Sales.
          </p>
        </li>
        <li>
          <p>
            <strong>Evidence</strong>: Mobile app launch succeeded due to early
            Sales input on customer needs; Operations efficiency gains from
            Engineering automation support; Marketing campaign effectiveness
            increased through Product team insights.
          </p>
        </li>
        <li>
          <p>
            <strong>Business Impact</strong>: Faster time-to-market, improved
            customer satisfaction, and better resource utilization.
          </p>
        </li>
        <li>
          <p>
            <strong>Leadership Opportunity</strong>: Formalize cross-functional
            working groups for major initiatives; create recognition systems for
            collaborative achievements.
          </p>
        </li>
      </ul>
      <h2 id="executive-discussion-guide" className="section-title">
        💬 EXECUTIVE DISCUSSION GUIDE
      </h2>
      <h3
        id="resource-allocation-for-sustainable-growth"
        className="discussion-topic"
      >
        <strong>Resource Allocation for Sustainable Growth</strong>
      </h3>
      <div className="column-gap-12px">
        <p>
          <strong>Context</strong>: Multiple teams reporting capacity
          constraints while business metrics show strong growth trajectory
        </p>
        <p>
          <strong>Questions</strong>:
        </p>
        <ul className="check-in-response-list">
          <li>
            <p>
              What's our optimal hiring sequence to support both immediate needs
              and long-term growth?
            </p>
          </li>
          <li>
            <p>
              How might we leverage automation or process improvements before
              adding headcount?
            </p>
          </li>
          <li>
            <p>
              Which departments need immediate relief versus those that can
              optimize existing resources?
            </p>
          </li>
        </ul>
      </div>
      <h3 id="customer-experience-during-scaling" className="discussion-topic">
        <strong>Customer Experience During Scaling</strong>
      </h3>
      <div className="column-gap-12px">
        <p>
          <strong>Context</strong>: Support team struggling with volume while
          maintaining quality standards
        </p>
        <p>
          <strong>Questions</strong>:
        </p>
        <ul className="check-in-response-list">
          <li>
            <p>
              What self-service capabilities could reduce ticket volume without
              compromising customer satisfaction?
            </p>
          </li>
          <li>
            <p>
              How do we balance response time goals with quality of resolution?
            </p>
          </li>
          <li>
            <p>
              What leading indicators should we monitor to prevent service
              degradation?
            </p>
          </li>
        </ul>
      </div>
      <h3
        id="innovation-vs.-operational-excellence"
        className="discussion-topic"
      >
        <strong>Innovation vs. Operational Excellence</strong>
      </h3>
      <div className="column-gap-12px">
        <p>
          <strong>Context</strong>: Teams balancing new initiatives with
          maintaining/improving existing systems
        </p>
        <p>
          <strong>Questions</strong>:
        </p>
        <ul className="check-in-response-list">
          <li>
            <p>
              What's the right resource allocation between innovation and
              operational stability?
            </p>
          </li>
          <li>
            <p>
              How do we ensure technical debt doesn't accumulate while pursuing
              growth?
            </p>
          </li>
          <li>
            <p>
              Which innovation projects have the highest ROI potential for next
              quarter?
            </p>
          </li>
        </ul>
      </div>
      <h2 id="forward-indicators" className="section-title">
        📈 FORWARD INDICATORS
      </h2>
      <ul className="check-in-response-list">
        <li>
          <p>
            New product feature adoption tracking at 78% among power users,
            indicating strong market fit
          </p>
        </li>
        <li>
          <p>
            Employee referral rate increasing (+15% this quarter) suggesting
            positive internal culture despite workload
          </p>
        </li>
        <li>
          <p>
            Customer satisfaction scores maintaining at 4.5/5 despite increased
            support volumes
          </p>
        </li>
        <li>
          <p>
            Three major enterprise deals in final negotiation stages could
            significantly impact Q3 revenue
          </p>
        </li>
      </ul>
      <h2 id="key-wins" className="section-title">
        🚀 KEY WINS
      </h2>
      <div className="column-gap-12px">
        <p>
          <strong>Product Launch Success</strong>: Mobile app update completed 2
          weeks ahead of schedule with immediate positive customer feedback and
          25% increase in daily active users
        </p>
        <p>
          <strong>Operational Efficiency</strong>: New automated workflow
          reduced order processing time by 40%, freeing up team capacity for
          strategic initiatives
        </p>
        <p className="mb-2">
          <strong>Sales Momentum</strong>: Team secured largest enterprise deal
          in company history while maintaining strong pipeline development for
          Q3
        </p>
      </div>
    </div>
  </Card>
);

export default ExecutiveReport;
